"use client"

import { useState, useCallback } from "react"
import { useMotionValue } from "motion/react"
import type { PanInfo, MotionValue } from "motion/react"

type UseDraggableDrawerOptions = {
    onClose: () => void
    /** Percentage of viewport height to trigger close (default: 0.15 = 15%) */
    closeThreshold?: number
    /** Velocity threshold to trigger close on fast swipe (default: 500) */
    velocityThreshold?: number
    /** Elasticity factor for upward drag resistance (default: 0.3) */
    upwardElasticity?: number
    /** Whether dragging is enabled (default: true) */
    enabled?: boolean
}

type UseDraggableDrawerReturn = {
    /** MotionValue for the Y offset - use this in motion.div style={{ y }} */
    y: MotionValue<number>
    /** Whether the drawer is currently being dragged */
    isDragging: boolean
    /** Handler for drag events */
    onDrag: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
    /** Handler for drag end events */
    onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
    /** Motion drag constraint config */
    dragConstraints: { top: number; bottom: number }
    /** Motion drag elasticity config */
    dragElastic: { top: number; bottom: number }
}

/**
 * A reusable hook for mobile-friendly drawer drag behavior.
 * 
 * Features:
 * - Drag down past threshold to close
 * - Elastic resistance when pulling up (diminishing returns)
 * - Velocity-based close on fast swipe
 * 
 * @example
 * ```tsx
 * const { y, onDrag, onDragEnd, dragConstraints, dragElastic } = useDraggableDrawer({
 *   onClose: handleClose,
 *   closeThreshold: 0.15
 * })
 * 
 * return (
 *   <motion.div
 *     drag="y"
 *     dragConstraints={dragConstraints}
 *     dragElastic={dragElastic}
 *     onDrag={onDrag}
 *     onDragEnd={onDragEnd}
 *     style={{ y }}
 *   >
 *     {children}
 *   </motion.div>
 * )
 * ```
 */
export function useDraggableDrawer({
    onClose,
    closeThreshold = 0.15,
    velocityThreshold = 500,
    upwardElasticity = 0.3,
    enabled = true
}: UseDraggableDrawerOptions): UseDraggableDrawerReturn {
    // Using MotionValue directly to avoid React re-render lag/flashing
    const y = useMotionValue(0)
    const [isDragging, setIsDragging] = useState(false)

    const onDrag = useCallback(
        (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
            if (!enabled) return

            setIsDragging(true)
            const offset = info.offset.y

            if (offset > 0) {
                // Dragging down - allow free movement
                y.set(offset)
            } else {
                // Dragging up - apply elastic resistance
                // Uses a logarithmic curve for natural "rubber band" feel
                const resistance = Math.log10(Math.abs(offset) + 1) * 30 * upwardElasticity
                y.set(-resistance)
            }
        },
        [enabled, upwardElasticity, y]
    )

    const onDragEnd = useCallback(
        (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
            if (!enabled) return

            setIsDragging(false)
            const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800
            const threshold = viewportHeight * closeThreshold

            // Close if:
            // 1. Dragged down past the threshold
            // 2. Fast swipe down (velocity exceeds threshold)
            const shouldClose =
                info.offset.y > threshold || info.velocity.y > velocityThreshold

            if (shouldClose) {
                onClose()
            }

            // Reset drag position
            y.set(0)
        },
        [enabled, closeThreshold, velocityThreshold, onClose, y]
    )

    return {
        y,
        isDragging,
        onDrag,
        onDragEnd,
        dragConstraints: { top: 0, bottom: 0 },
        dragElastic: { top: upwardElasticity, bottom: 0.5 }
    }
}
